import React, { useState, useEffect } from 'react';
import { fetchDepartments as fetchDepartmentsAPI, createDepartmentWithREST } from '../../api/packageService';
import AddDepartmentModal from './AddDepartmentModal';

interface Department {
  _id: string;
  name?: string;
  departmentName?: string;
  companyId?: string | {
    _id: string;
    companyName?: string;
    name?: string;
  };
  company?: string;
  companyName?: string;
  createdAt?: string;
  created_at?: string;
  updatedAt?: string;
  updated_at?: string;
}

const DepartmentsList: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modal state
  const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);

  // const navigate = useNavigate();
  // const location = useLocation();

  useEffect(() => {
    const fetchDepartmentsData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('AUTH_TOKEN');
        if (!token) {
          setError('User not authenticated');
          setLoading(false);
          return;
        }

        const response = await fetchDepartmentsAPI();
        
        if (response.departments) {
          // Check if response.departments is a single object or an array
          if (Array.isArray(response.departments)) {
            setDepartments(response.departments);
          } else if (typeof response.departments === 'object' && response.departments !== null) {
            // If it's a single department object, wrap it in an array
            setDepartments([response.departments]);
          } else {
            setDepartments([]);
          }
        } else {
          setError('Failed to fetch departments');
        }
      } catch (err) {
        console.error('Error fetching departments:', err);
        setError('Error fetching departments: ' + (err instanceof Error ? err.message : 'Unknown error'));
      } finally {
        setLoading(false);
      }
    };

    fetchDepartmentsData();
  }, []);

  // Filter departments based on search term
  const filteredDepartments = departments.filter(department =>
    (department.name && department.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (department.companyId && 
      (typeof department.companyId === 'string' 
        ? department.companyId.toLowerCase().includes(searchTerm.toLowerCase())
        : (department.companyId.companyName || department.companyId.name || '').toLowerCase().includes(searchTerm.toLowerCase()))
    )
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredDepartments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedDepartments = filteredDepartments.slice(startIndex, startIndex + itemsPerPage);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // const handleAddDepartment = () => {
  //   setShowAddDepartmentModal(true);
  // };

  return (
    <>
      <div className="flex-grow-1 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
        <div className="card border-0 shadow-sm rounded-3 overflow-hidden flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
          <div className="card-header bg-white border-0 py-2">
            <div className="d-flex justify-content-between align-items-center flex-column flex-md-row text-start w-100">
              <div className="text-start mb-2 mb-md-0 w-100" style={{ textAlign: 'left' }}>
                <h2 className="h5 fw-bold text-dark mb-0">Departments Management</h2>
              </div>
              <div className="d-flex flex-row gap-2 w-100 w-md-auto flex-wrap justify-content-end">
                <div className="input-group" style={{ maxWidth: '300px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search departments..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                  />
                  <span className="input-group-text">
                    <i className="bi bi-search"></i>
                  </span>
                </div>
                <button
                  className="btn btn-primary rounded-pill px-2 px-md-2 py-1 py-md-2 flex-shrink-0"
                  onClick={() => setShowAddDepartmentModal(true)}
                  style={{
                    fontSize: '0.875rem',
                    color: '#ffffff',
                    background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
                    border: 'none'
                  }}
                >
                  <i className="bi bi-plus-lg me-1" style={{ color: '#ffffff' }}></i>
                  <span className="d-none d-sm-inline" style={{ color: '#ffffff' }}>Add Department</span>
                  <span className="d-sm-none" style={{ color: '#ffffff' }}>Add</span>
                </button>
              </div>
            </div>
          </div>
          <div className="card-body p-0 flex-grow-1 d-flex flex-column" style={{ minHeight: 0 }}>
            <div className="table-container rounded-3 shadow-sm flex-grow-1 d-flex flex-column" style={{ height: '100%', minHeight: 0 }}>
              {loading ? (
                <div className="text-center py-5 flex-grow-1 d-flex flex-column justify-content-center">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Loading departments...</p>
                </div>
              ) : error ? (
                <div className="alert alert-danger m-4 flex-grow-1 d-flex flex-column justify-content-center">
                  {error}
                </div>
              ) : (
                <>
                  <div className="table-wrapper-mobile flex-grow-1" style={{ overflow: 'auto', minHeight: 0 }}>
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-primary text-dark sticky-top" style={{ zIndex: 10 }}>
                        <tr>
                          <th className="py-2 px-2 px-md-2 text-start">#</th>
                          <th className="py-2 px-2 px-md-2 text-start">Department Name</th>
                          <th className="py-2 px-2 px-md-2 text-start">Company ID</th>
                          <th className="py-2 px-2 px-md-2 d-none d-md-table-cell text-start">Created At</th>
                          <th className="py-2 px-2 px-md-2 text-start">Updated At</th>
                          <th className="py-2 px-2 px-md-2 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedDepartments.map((department, index) => {
                          // Ensure department is a valid object with required properties
                          if (!department || typeof department !== 'object' || !department._id) {
                            return null;
                          }
                          
                          return (
                            <tr key={department._id}>
                              <td className="py-2 px-2 px-md-2 text-start">
                                <span className="fw-medium">{startIndex + index + 1}</span>
                              </td>
                              <td className="py-2 px-2 px-md-2 text-start">
                                <span className="fw-medium">{department.name || department.departmentName || 'N/A'}</span>
                              </td>
                              {/* <td className="py-2 px-2 px-md-2 text-start">
                                {(department.companyId && typeof department.companyId === 'object') ? 
                                  (department.companyId.companyName || department.companyId.name || 'N/A') : 
                                  (department.companyId || department.company || 'N/A')}
                              </td> */}
                              <td className="py-2 px-2 px-md-2 d-none d-md-table-cell text-start">
                                {(department.createdAt || department.created_at) ? new Date(department.createdAt || department.created_at as string).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="py-2 px-2 px-md-2 text-start">
                                {(department.updatedAt || department.updated_at) ? new Date(department.updatedAt || department.updated_at as string).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="text-center py-2 px-2 px-md-2">
                                <div className="d-flex justify-content-center gap-1 gap-md-2">
                                  <button 
                                    className="btn btn-outline-primary btn-sm rounded-pill px-2 px-md-3" 
                                    title="Edit Department"
                                  >
                                    <i className="bi bi-pencil"></i>
                                  </button>
                                  <button 
                                    className="btn btn-outline-danger btn-sm rounded-pill px-2 px-md-3" 
                                    title="Delete Department"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }).filter(Boolean)}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center p-2 p-md-2 table-pagination-mobile" style={{ flexShrink: 0 }}>
                    <div className="d-flex align-items-center flex-wrap">
                      <span className="me-2 small">Items per page:</span>
                      <select
                        className="form-select form-select-sm"
                        value={itemsPerPage}
                        disabled
                        style={{ width: '80px' }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="ms-2 ms-md-3 small">Total: {filteredDepartments.length} departments</span>
                    </div>
                    
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>
                            <span className="d-none d-sm-inline">Previous</span>
                            <span className="d-sm-none">Prev</span>
                          </button>
                        </li>
                        
                        {/* Generate page numbers */}
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          
                          return (
                            <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                              <button className="page-link" onClick={() => handlePageChange(pageNum)}>
                                {pageNum}
                              </button>
                            </li>
                          );
                        })}
                        
                        {totalPages > 5 && currentPage < totalPages - 2 && (
                          <>
                            <li className="page-item disabled"><span className="page-link">...</span></li>
                            <li className="page-item">
                              <button className="page-link" onClick={() => handlePageChange(totalPages)}>
                                {totalPages}
                              </button>
                            </li>
                          </>
                        )}
                        
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>
                            <span className="d-none d-sm-inline">Next</span>
                            <span className="d-sm-none">Next</span>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {showAddDepartmentModal && (
        <AddDepartmentModal 
          isOpen={showAddDepartmentModal}
          onClose={() => setShowAddDepartmentModal(false)}
          onSubmit={async (departmentData) => {
            try {
              await createDepartmentWithREST(departmentData);
              // Refresh the departments list after successful creation
              const token = localStorage.getItem('AUTH_TOKEN');
              if (!token) {
                setError('User not authenticated');
                return;
              }

              const response = await fetchDepartmentsAPI();

              if (response.departments) {
                // Check if response.departments is a single object or an array
                if (Array.isArray(response.departments)) {
                  setDepartments(response.departments);
                } else if (typeof response.departments === 'object' && response.departments !== null) {
                  // If it's a single department object, wrap it in an array
                  setDepartments([response.departments]);
                } else {
                  setDepartments([]);
                }
              }

              setShowAddDepartmentModal(false);
            } catch (err) {
              console.error('Error creating department:', err);
              setError('Error creating department: ' + (err instanceof Error ? err.message : 'Unknown error'));
            }
          } }
          formLoading={false}
          formError={error}
          formSuccess={null} companies={[]} loadingCompanies={false} userRole={'ADMIN'}        />
      )}
    </>
  );
};

export default DepartmentsList;